import { mutationOptions, queryOptions } from '@tanstack/react-query'
import type { PeopleSubSections } from 'src/enums'
import type { QueryError } from 'src/queries/network.utils'
import type {
  PostExpenseReportsDownloadRequestParams,
  PostOrgExpenseReportDownloadRequestParams,
  PostReportTransactionsDownloadParams,
  PostReportVirtualCardsDownloadParams,
  ReportDownloadResponse,
} from 'src/queries/reportDownloads/reportDownloads.models'
import {
  getReportDownload,
  postExpenseReportDownload,
  postOrgExpenseReportDownload,
  postPeopleDownload,
  postReportTransactionsDownload,
  postReportVirtualCardsDownload,
} from 'src/queries/reportDownloads/reportDownloads.network'

export const reportDownloadsQueryKeys = {
  all: () => ['reportDownloads'] as const,
  detail: (reportDownloadId: string | undefined) =>
    [
      ...reportDownloadsQueryKeys.all(),
      'detail',
      reportDownloadId,
    ] as const,
}

const getReportDownloadById = (reportDownloadId: string | undefined) => {
  if (!reportDownloadId) {
    throw new Error('Report download id is required')
  }

  return getReportDownload(reportDownloadId)
}

export const reportDownloadsQueries = {
  detail: (reportDownloadId: string | undefined) =>
    queryOptions({
      queryKey: reportDownloadsQueryKeys.detail(reportDownloadId),
      queryFn: () => getReportDownloadById(reportDownloadId),
      enabled: Boolean(reportDownloadId),
      retry: false,
    }),
}

export const reportDownloadsMutations = {
  transactions: () =>
    mutationOptions<
      ReportDownloadResponse,
      QueryError,
      PostReportTransactionsDownloadParams
    >({
      mutationFn: (params) => postReportTransactionsDownload(params),
    }),
  virtualCards: () =>
    mutationOptions<
      ReportDownloadResponse,
      QueryError,
      PostReportVirtualCardsDownloadParams
    >({
      mutationFn: (params) => postReportVirtualCardsDownload(params),
    }),
  expenseReport: () =>
    mutationOptions<
      ReportDownloadResponse,
      QueryError,
      PostExpenseReportsDownloadRequestParams | undefined
    >({
      mutationFn: (params) => postExpenseReportDownload(params),
    }),
  orgExpenseReport: () =>
    mutationOptions<
      ReportDownloadResponse,
      QueryError,
      PostOrgExpenseReportDownloadRequestParams
    >({
      mutationFn: (params) => postOrgExpenseReportDownload(params),
    }),
  people: () =>
    mutationOptions<ReportDownloadResponse, QueryError, PeopleSubSections>({
      mutationFn: (type) => postPeopleDownload(type),
    }),
}
